import { Fragment, useEffect, useState } from "react";

import { api, ApiError } from "../api/client";
import { formatBytes, formatDateTime } from "../lib/format";
import { ui } from "../lib/ui";
import type { Download, DownloadCamera, FootageCamera, NvrError } from "../types";
import { CopyButton } from "./CopyButton";
import { ProgressBar } from "./ProgressBar";

interface DayCamerasProps {
  branchId: string;
  cameras: FootageCamera[];
  /** The download for this day, if any (newest). */
  download: Download | null;
  /** After a channel was saved. */
  onChanged: () => void;
}

/** Every camera of the store for one day: its NVR channel (editable), what a
 *  run would read for it, and — while downloading — how far it got. */
export function DayCameras({ branchId, cameras, download, onChanged }: DayCamerasProps) {
  if (cameras.length === 0) {
    return (
      <p className="text-muted">
        No cameras yet — place them on the floor plan (Store → Floor plan &amp;
        cameras) and set up the entrance ones.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className={ui.table}>
        <thead>
          <tr>
            <th>Camera</th>
            <th title="The channel this camera records on in the NVR (1, 2, … or a track id such as 201)">
              NVR channel
            </th>
            <th>Footage for this day</th>
            <th className="w-56">Download</th>
          </tr>
        </thead>
        <tbody>
          {cameras.map((camera) => {
            const progress = download?.cameras.find((c) => c.camera_id === camera.camera_id);
            return (
            <Fragment key={camera.camera_id}>
            <tr>
              <td>
                <span className="font-mono text-xs">{camera.camera_id}</span>
                {camera.label && camera.label !== camera.camera_id && (
                  <span className="ml-1.5 text-muted">{camera.label}</span>
                )}
                {camera.entrance && (
                  <span className={`${ui.pill} ${ui.pillRunning} ml-1.5`} title="Counted: entry line set up">
                    entrance
                  </span>
                )}
              </td>
              <td>
                <ChannelInput
                  branchId={branchId}
                  cameraId={camera.camera_id}
                  channel={camera.channel}
                  onSaved={onChanged}
                />
              </td>
              <td>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`${ui.pill} ${camera.videos > 0 ? ui.pillSucceeded : ""}`}
                    title={camera.source === "linked" ? "Read in place from its linked folder" : "Its subfolder of the day folder"}
                  >
                    {camera.videos} video{camera.videos === 1 ? "" : "s"}
                  </span>
                  <code className="font-mono text-[12px] break-all text-muted">{camera.path}</code>
                  <CopyButton text={camera.path} label="Copy" className={`${ui.btnQuiet} px-2! py-1! text-xs`} />
                </div>
              </td>
              <td>
                <CameraProgress camera={progress} />
              </td>
            </tr>
            {progress && (progress.status === "failed" || progress.errors.length > 0) && (
              <tr>
                <td colSpan={4}>
                  <DownloadErrorDetails camera={progress} />
                </td>
              </tr>
            )}
            </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ChannelInput({
  branchId,
  cameraId,
  channel,
  onSaved,
}: {
  branchId: string;
  cameraId: string;
  channel: string | null;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(channel ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(channel ?? ""), [channel]);
  useEffect(() => {
    if (state !== "saved") return;
    const timer = setTimeout(() => setState("idle"), 1500);
    return () => clearTimeout(timer);
  }, [state]);

  const save = async () => {
    const value = draft.trim();
    if (value === (channel ?? "")) return;
    setState("saving");
    setError(null);
    try {
      await api.setCameraChannel(branchId, cameraId, value === "" ? null : value);
      setState("saved");
      onSaved();
    } catch (err) {
      setState("idle");
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <input
        className={`${ui.input} w-16 py-1! font-mono text-xs`}
        inputMode="numeric"
        value={draft}
        placeholder="—"
        aria-label={`NVR channel of ${cameraId}`}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      {state === "saving" && <span className="text-xs text-muted">…</span>}
      {state === "saved" && <span className="text-xs text-success">✓</span>}
      {error && (
        <span className="text-xs text-danger" title={error}>
          !
        </span>
      )}
    </div>
  );
}

/** Everything known about why a camera's download failed, collapsed until
 *  asked for: what the NVR or the network answered, then the script's output. */
function DownloadErrorDetails({ camera }: { camera: DownloadCamera }) {
  const report = [
    `Camera ${camera.camera_id}, NVR channel ${camera.channel}, script exit code ${camera.exit_code ?? "—"}`,
    ...camera.errors.map(errorReport),
    "Script output:",
    ...camera.log,
  ].join("\n\n");
  return (
    <details className="text-[13px]">
      <summary className="cursor-pointer text-danger">
        Error details for {camera.camera_id}
        {camera.errors.length > 0 &&
          ` (${camera.errors.length} error${camera.errors.length === 1 ? "" : "s"})`}
      </summary>
      <div className="mt-2 flex flex-col gap-3 rounded-lg border border-border bg-bg p-3">
        <div className="flex items-center justify-between gap-3 text-xs text-muted">
          <span>
            NVR channel {camera.channel} · script exit code {camera.exit_code ?? "—"}
          </span>
          <CopyButton text={report} label="Copy all" className={`${ui.btnQuiet} px-2! py-1! text-xs`} />
        </div>
        {[...camera.errors].reverse().map((error, index) => (
          <NvrErrorBlock key={index} error={error} />
        ))}
        {camera.errors.length === 0 && (
          <p className="m-0 text-muted">
            The script stopped without reporting a specific request failing. Its output below says
            how far it got.
          </p>
        )}
        <div>
          <p className={`${ui.label} m-0 mb-1`}>Script output</p>
          <pre className="m-0 max-h-60 overflow-auto rounded-md border border-border bg-surface p-2 font-mono text-[11px] whitespace-pre-wrap break-all">
            {camera.log.join("\n") || "No output."}
          </pre>
        </div>
      </div>
    </details>
  );
}

function errorFields(error: NvrError): [string, string][] {
  const fields: [string, string | number | null][] = [
    ["Request", error.url ? `POST ${error.url}` : null],
    ["File", error.segment],
    ["Attempt", error.attempt],
    [
      "HTTP answer",
      error.http_status === null || error.http_status === undefined
        ? null
        : `${error.http_status} ${error.http_reason ?? ""}`.trim(),
    ],
    ["Network status", error.web_status],
    ["Message", error.message],
    ["Exception", error.type],
    ["Caused by", error.inner],
    ["Response headers", error.headers],
    ["Response body", error.body],
  ];
  return fields
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([label, value]) => [label, String(value)]);
}

function errorReport(error: NvrError): string {
  return [
    `${error.at ?? ""} ${error.stage ?? ""} failed${error.hint ? `: ${error.hint}` : ""}`.trim(),
    ...errorFields(error).map(([label, value]) => `${label}: ${value}`),
  ].join("\n");
}

function NvrErrorBlock({ error }: { error: NvrError }) {
  return (
    <div>
      <p className="m-0 mb-1 font-semibold text-danger">
        {error.stage ? `${error.stage[0].toUpperCase()}${error.stage.slice(1)} failed` : "Failed"}
        {error.at ? ` · ${formatDateTime(error.at)}` : ""}
      </p>
      {error.hint && <p className="m-0 mb-1.5">{error.hint}</p>}
      <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
        {errorFields(error).map(([label, value]) => (
          <Fragment key={label}>
            <dt className="text-muted">{label}</dt>
            <dd className="m-0 max-h-48 overflow-auto font-mono whitespace-pre-wrap break-all">
              {value}
            </dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

const CAMERA_TONE = { done: "success", failed: "danger" } as const;

function CameraProgress({ camera }: { camera: DownloadCamera | undefined }) {
  if (!camera) return <span className="text-muted">—</span>;
  let value: number | null = null;
  if (camera.status === "done") value = 1;
  else if (camera.bytes_total) value = camera.bytes_done / camera.bytes_total;
  else if (camera.segments) value = (camera.segments_done + camera.segments_failed) / camera.segments;
  else if (camera.status === "waiting" || camera.status === "cancelled") value = 0;
  else if (camera.status === "failed") value = 1;

  const counts =
    camera.segments === null
      ? null
      : `${camera.segments_done}/${camera.segments} file${camera.segments === 1 ? "" : "s"}`;
  const size = camera.bytes_total
    ? `${formatBytes(camera.bytes_done)} of ${formatBytes(camera.bytes_total)}`
    : null;
  const note = {
    waiting: "waiting",
    searching: "searching the NVR…",
    downloading: [counts, size].filter(Boolean).join(" · "),
    done: camera.segments === 0 ? "nothing recorded that day" : [counts, size].filter(Boolean).join(" · "),
    failed: camera.note ?? `${camera.segments_failed} file(s) failed`,
    cancelled: "cancelled",
  }[camera.status];

  return (
    <div title={camera.note ?? camera.current ?? undefined}>
      <ProgressBar
        value={value}
        tone={CAMERA_TONE[camera.status as keyof typeof CAMERA_TONE] ?? "accent"}
        note={note}
      />
      {camera.note && camera.status !== "failed" && (
        <p className="mt-0.5 text-[11px] text-danger">{camera.note}</p>
      )}
    </div>
  );
}
